
import React, { useState, useRef, useEffect } from 'react';
import { Search, MapPin, AlertCircle, X, Check } from 'lucide-react';

interface LocationSearchFormProps {
  onLocationSelect: (location: any) => void;
  selectedSpots?: any[];
  onRemoveSpot?: (spotId: string) => void;
  onSubmit?: (spots: any[]) => void;
}

const LocationSearchForm: React.FC<LocationSearchFormProps> = ({ 
  onLocationSelect, 
  selectedSpots = [], 
  onRemoveSpot,
  onSubmit 
}) => {
  const [query, setQuery] = useState('');
  const [predictions, setPredictions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showPredictions, setShowPredictions] = useState(false);
  const [error, setError] = useState('');
  const [showNotification, setShowNotification] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const searchPlaces = (searchQuery: string) => {
      if (!searchQuery.trim()) {
        setPredictions([]);
        setShowPredictions(false);
        setError('');
        return;
      }

      // Check if Google Maps API is loaded
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        setError('Google Maps is still loading. Please wait...');
        return;
      }

      setIsLoading(true);
      setError('');

      try {
        const autocompleteService = new window.google.maps.places.AutocompleteService();
        
        autocompleteService.getPlacePredictions(
          {
            input: searchQuery,
            componentRestrictions: { country: 'GB' },
            types: ['establishment']
          },
          (predictions, status) => {
            setIsLoading(false);
            console.log('Places API status:', status);
            
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
              setPredictions(predictions);
              setShowPredictions(true);
              setError('');
            } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
              setPredictions([]);
              setShowPredictions(false);
              setError('No places found. Try a different search term.');
            } else if (status === 'REQUEST_DENIED') {
              setPredictions([]);
              setShowPredictions(false);
              setError('API access denied. Please check API key configuration.');
            } else {
              console.error('Places API error status:', status);
              setPredictions([]);
              setShowPredictions(false);
              setError(`Search failed (${status}). Please try again.`);
            }
          }
        );
      } catch (err) {
        console.error('Places API error:', err);
        setIsLoading(false);
        setError('Search service unavailable. Please try again later.');
      }
    };

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      searchPlaces(query);
    }, 300);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [query]);

  const handlePlaceSelect = (prediction: any) => {
    // Check if Google Maps API is loaded
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      setError('Google Maps is still loading. Please wait...');
      return;
    }

    try {
      const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
      
      placesService.getDetails(
        {
          placeId: prediction.place_id,
          fields: ['name', 'formatted_address', 'place_id', 'types', 'geometry']
        },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            onLocationSelect({
              id: Date.now().toString(),
              name: place.name,
              address: place.formatted_address,
              placeId: place.place_id,
              types: place.types,
              coordinates: place.geometry?.location ? {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng()
              } : null
            });
            setQuery('');
            setPredictions([]);
            setShowPredictions(false);
            setError('');
          } else {
            setError('Unable to get place details. Please try another location.');
          }
        }
      );
    } catch (err) {
      console.error('Place details error:', err);
      setError('Unable to get place details. Please try again.');
    }
  };

  const handleSubmit = () => {
    if (selectedSpots.length > 0 && onSubmit) {
      onSubmit(selectedSpots);
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 3000);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-8 w-full max-w-6xl mx-auto">
      {/* Search Section */}
      <div className="flex-1">
        <div className="relative w-full max-w-md mx-auto lg:mx-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for cafes, libraries, coworking spaces..."
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              onFocus={() => query && setShowPredictions(true)}
              onBlur={() => setTimeout(() => setShowPredictions(false), 200)}
            />
            {isLoading && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
              </div>
            )}
          </div>

          {error && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center gap-2 text-red-600 text-sm">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            </div>
          )}

          {showPredictions && predictions.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {predictions.map((prediction) => (
                <button
                  key={prediction.place_id}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 focus:outline-none focus:bg-gray-50"
                  onClick={() => handlePlaceSelect(prediction)}
                >
                  <div className="flex items-start gap-3">
                    <MapPin size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">
                        {prediction.structured_formatting.main_text}
                      </div>
                      <div className="text-sm text-gray-500 truncate">
                        {prediction.structured_formatting.secondary_text}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Selected Spots Sidebar */}
      <div className="lg:w-80">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-4">Selected Spots ({selectedSpots.length})</h3>
          
          {selectedSpots.length === 0 ? (
            <p className="text-gray-500 text-sm">No spots selected yet. Search and select locations above.</p>
          ) : (
            <div className="space-y-3 mb-4">
              {selectedSpots.map((spot) => (
                <div key={spot.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <MapPin size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate text-sm">
                      {spot.name}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {spot.address}
                    </div>
                  </div>
                  {onRemoveSpot && (
                    <button
                      onClick={() => onRemoveSpot(spot.id)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {selectedSpots.length > 0 && (
            <button
              onClick={handleSubmit}
              className="w-full bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600 transition-colors font-medium"
            >
              Submit Suggestions ({selectedSpots.length})
            </button>
          )}
        </div>
      </div>

      {/* Success Notification */}
      {showNotification && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 z-50">
          <Check size={20} />
          <span>Spots submitted successfully!</span>
        </div>
      )}
    </div>
  );
};

export default LocationSearchForm;
